import useOffers from 'hooks/Offers/useOffers'
import { useAppSelector } from 'hooks/reduxHooks'
import { isCoverGeniusEnabled, isCoverGeniusEndDateValid, isInsuranceStartDateValid } from 'lib/insurance/insuranceHelpers'
import { getInsuranceDatesFromOrder, getInsuranceDestinationCountriesFromOrder } from 'lib/order/orderInsuranceUtils'
import { getCanViewLuxPlusBenefits, getIsEnabledLuxPlusDiscountedInsurance } from 'luxPlus/selectors/featureToggle'
import { useMemo } from 'react'
import isInsuranceBookingProtectionPostPurchaseEnabled from 'selectors/isInsuranceBookingProtectionPostPurchaseEnabled'

function useInsuranceUpsellCommonData(order: App.Order) {
  // Luxplus
  const fetchingLuxPlusSubscription = useAppSelector(state => state.auth.account.luxPlus.member.subscription.fetching)
  const ableToViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const luxPlusDiscountedInsuranceEnabled = useAppSelector(getIsEnabledLuxPlusDiscountedInsurance)

  // Ofers
  const offerIds = useMemo(() => [
    ...order.items.map((i) => i.offer.id),
    ...order.cruiseItems.map((i) => i.cruiseOfferId),
  ], [order.cruiseItems, order.items])
  const [offers, fetching] = useOffers(offerIds)

  // Insurance Dates
  const dates = useMemo(() => getInsuranceDatesFromOrder(order), [order])
  const datesAreValid = dates?.endDate && dates.startDate ?
    isInsuranceStartDateValid(dates.startDate) && isCoverGeniusEndDateValid(dates.endDate) : false

  // Insurance destination countries
  const cruiseOffers = useAppSelector(state => state.cruise.cruiseOfferSnapshot)
  const destCountriesFromOrder = useMemo(() => getInsuranceDestinationCountriesFromOrder(order, cruiseOffers), [cruiseOffers, order])

  // Insurance Booking Protection flags
  const isInsuranceBookingProtectionSupported = useAppSelector(state => isInsuranceBookingProtectionPostPurchaseEnabled(state, order))

  // Insurance flags
  const coverGeniusIsEnabled = useAppSelector(state => isCoverGeniusEnabled(state.geo.currentRegionCode))
  const isInsuranceSupported = !!(dates && destCountriesFromOrder.length) && coverGeniusIsEnabled && !isInsuranceBookingProtectionSupported
  const orderHasInsurance = order.hasInsurance

  return {
    luxplus: {
      fetchingLuxPlusSubscription,
      ableToViewLuxPlusBenefits,
      luxPlusDiscountedInsuranceEnabled,
    },
    offers: {
      offers,
      fetching,
    },
    datesAreValid,
    flags: {
      isInsuranceSupported,
      orderHasInsurance,
      isInsuranceBookingProtectionSupported,
    },
  }
}

export default useInsuranceUpsellCommonData
